import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/vue'

import router from '@/router'
import pinia from '@/stores/pinia'
import { STORAGE_KEYS } from '@/constants/storage'
import { normalizeApiError } from './error'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Accept: 'application/json',
  },
})

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { useAuthStore } = await import('@/modules/auth/stores/auth.store')
  const { useStudioStore } = await import('@/modules/studio/stores/studio.store')

  const authStore = useAuthStore(pinia)
  const studioStore = useStudioStore(pinia)

  const token = authStore.token || localStorage.getItem(STORAGE_KEYS.token)
  const studioId =
    studioStore.currentStudioId ||
    localStorage.getItem(STORAGE_KEYS.studioId) ||
    localStorage.getItem('active_studio_id')

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (!config.headers['X-Studio-Id'] && studioId) {
    config.headers['X-Studio-Id'] = studioId
  }

  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { useAuthStore } = await import('@/modules/auth/stores/auth.store')
    const authStore = useAuthStore(pinia)

    if (error.response?.status === 401) {
      authStore.logout({ skipRequest: true })
      if (router.currentRoute.value.path !== '/login') {
        await router.replace({ path: '/login' })
      }
    }

    if (error.response?.status === 403) {
      window.alert('No autorizado')
    }

    // 402 Payment Required: marcar el error para que el guard/componente decida.
    // No redirigimos aquí — el guard de ruta ya maneja el acceso.
    if (error.response?.status === 402) {
      const data = error.response.data as { subscription_status?: string } | undefined
      ;(error as AxiosError & { isPaymentRequired?: boolean; subscriptionStatus?: string }).isPaymentRequired = true
      ;(error as AxiosError & { isPaymentRequired?: boolean; subscriptionStatus?: string }).subscriptionStatus =
        data?.subscription_status ?? 'unknown'
    }

    // Capturar errores 5xx en Sentry — 4xx son comportamiento esperado de la app
    const status = error.response?.status
    if (status && status >= 500) {
      Sentry.captureException(error, {
        tags: {
          api_url:     error.config?.url    ?? 'unknown',
          api_method:  (error.config?.method ?? 'unknown').toUpperCase(),
          http_status: String(status),
        },
      })
    }

    const payload = error.response?.data as { errors?: unknown } | undefined
    if (payload?.errors) {
      ;(error as AxiosError & { normalizedErrors?: ReturnType<typeof normalizeApiError> }).normalizedErrors =
        normalizeApiError(payload as { errors: { code: string; detail: string; field?: string }[] })
    }

    return Promise.reject(error)
  },
)

export default apiClient
